import {
    EmailEditorConfig,
    EmailTemplateDetail,
    EmailTemplateSaveInput,
    AudienceCustomersPage,
    AudienceCustomersQuery,
    AudienceEstimate,
    AudienceGroupCounters,
    AudienceSelection,
    CampaignAudience,
    EmailCampaignCounts,
    EmailMarketingKpis,
    EmailMarketingKpisFilters,
    EmailMarketingLocation,
    EmailMarketingSender,
    EmailMarketingSenderStatus,
    EmailMarketingTemplateOption,
    LocationCampaignStatus,
    OrganizationCampaign,
    OrganizationCampaignInput,
    OrganizationCampaignLocation,
    OrganizationCampaignStatus
} from 'model/emailMarketing';
import {
    EmailEditorConfigDto,
    EmailTemplateDetailDto,
    EmailTemplateSaveDto,
    AudienceCustomersDto,
    AudienceEstimateDto,
    AudienceGroupCountersDto,
    AudienceQueryDto,
    EmailCampaignCountsDto,
    EmailMarketingKpisDto,
    EmailMarketingKpisQueryDto,
    EmailMarketingLocationDto,
    EmailMarketingSenderDto,
    EmailMarketingTemplateOptionDto,
    OrganizationCampaignDto,
    OrganizationCampaignInputDto,
    OrganizationCampaignLocationDto,
    RecipientSelectionDto
} from './EmailMarketingDtos';

const AUDIENCES: Array<CampaignAudience> = ['customers', 'leads', 'both'];
const CAMPAIGN_STATUSES: Array<OrganizationCampaignStatus> = ['draft', 'sending', 'sent', 'failed'];
const LOCATION_STATUSES: Array<LocationCampaignStatus> = ['draft', 'scheduled', 'sending', 'sent', 'failed'];
const SENDER_STATUSES: Array<EmailMarketingSenderStatus> = ['unconfigured', 'pending', 'verified', 'failed'];

// Unknown values fall back to the first option instead of breaking the UI.
const pick = <T extends string>(value: string, options: Array<T>): T =>
    options.includes(value as T) ? (value as T) : options[0];

const convertToCounts = (dto: Partial<EmailCampaignCountsDto>): EmailCampaignCounts => ({
    recipients: dto.recipients ?? 0,
    delivered: dto.delivered ?? 0,
    opens: dto.opens ?? 0,
    clicks: dto.clicks ?? 0,
    unsubscribes: dto.unsubscribes ?? 0,
    bounces: dto.bounces ?? 0,
    openRate: dto.open_rate ?? 0,
    clickRate: dto.click_rate ?? 0
});

const convertToCampaignLocation = (dto: OrganizationCampaignLocationDto): OrganizationCampaignLocation => ({
    emailCampaignId: dto.email_campaign_id,
    locationId: dto.marketplace_id,
    locationName: dto.location_name ?? '',
    status: pick(dto.status_id, LOCATION_STATUSES),
    fromName: dto.from_name,
    senderEmail: dto.sender_email,
    // The list doesn't return counts; the detail adds them to each location.
    ...(dto.recipients !== undefined && { counts: convertToCounts(dto) })
});

const splitArgs = (value: string | null | undefined): Array<string> =>
    (value ?? '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

const convertToSelection = (dto: RecipientSelectionDto | undefined): OrganizationCampaign['selection'] =>
    dto?.mode === 'include'
        ? { criteria: 'all', tags: [], excludeIds: [], customerIds: dto.customer_ids ?? [] }
        : {
              criteria: dto?.criteria === 'by_tag' ? 'by_tag' : 'all',
              tags: splitArgs(dto?.criteria_args),
              excludeIds: dto?.exclude_ids ?? [],
              customerIds: null
          };

export const convertToOrganizationCampaign = (dto: OrganizationCampaignDto): OrganizationCampaign => ({
    id: dto.id,
    uuid: dto.uuid,
    name: dto.name,
    subject: dto.subject,
    previewText: dto.preview_text ?? null,
    audience: pick(dto.population, AUDIENCES),
    templateId: dto.email_template_id,
    status: pick(dto.status_id, CAMPAIGN_STATUSES),
    sentAt: dto.sent_at,
    createdBy: dto.created_by ?? null,
    updatedAt: dto.updated_at,
    selection: convertToSelection(dto.recipient_selection),
    locations: (dto.locations ?? []).map(convertToCampaignLocation),
    ...(dto.totals && { totals: convertToCounts(dto.totals) })
});

export const convertToOrganizationCampaignInputDto = (
    input: OrganizationCampaignInput
): OrganizationCampaignInputDto => ({
    organization_campaign: {
        ...(input.name !== undefined && { name: input.name }),
        ...(input.subject !== undefined && { subject: input.subject }),
        ...(input.previewText !== undefined && { preview_text: input.previewText }),
        ...(input.audience !== undefined && { population: input.audience }),
        ...(input.templateId !== undefined && { email_template_id: input.templateId }),
        ...(input.locationIds !== undefined && { marketplace_ids: input.locationIds }),
        // Include mode sends only customer_ids (the API switches mode when criteria is absent).
        ...(input.selection &&
            (input.selection.customerIds !== null
                ? { customer_ids: input.selection.customerIds }
                : {
                      criteria: input.selection.criteria,
                      criteria_args: input.selection.tags.join(','),
                      exclude_ids: input.selection.excludeIds
                  }))
    }
});

export const convertToEmailMarketingLocation = (dto: EmailMarketingLocationDto): EmailMarketingLocation => ({
    id: dto.id,
    name: dto.business_name,
    city: dto.city ?? null,
    state: dto.state ?? null,
    verifiedSender: dto.verified_sender,
    senderEmail: dto.sender_email
});

export const convertToTemplateOption = (dto: EmailMarketingTemplateOptionDto): EmailMarketingTemplateOption => ({
    id: dto.id,
    uuid: dto.uuid,
    name: dto.name,
    category: dto.category,
    subjectDefault: dto.subject_default,
    system: dto.system,
    locationId: dto.marketplace_id ?? null,
    html: dto.html ?? null
});

export const convertToKpis = (dto: EmailMarketingKpisDto): EmailMarketingKpis => ({
    days: dto.days,
    emailsSent: dto.emails_sent,
    opens: dto.opens,
    clicks: dto.clicks,
    averageOpenRate: dto.average_open_rate,
    averageClickRate: dto.average_click_rate
});

// Omitting the locations means every location in the organization.
export const toKpisQueryDto = (filters: EmailMarketingKpisFilters): EmailMarketingKpisQueryDto => ({
    ...(filters.locationIds?.length && { marketplace_ids: filters.locationIds.join(',') }),
    ...(filters.days && { days: filters.days })
});

// A disabled sender can't send even if its email was verified, so it shows as not set up.
export const convertToSender = (dto: EmailMarketingSenderDto): EmailMarketingSender => ({
    locationId: dto.marketplace_id,
    locationName: dto.business_name,
    enabled: dto.enabled,
    senderEmail: dto.sender_email,
    status: dto.enabled ? pick(dto.status, SENDER_STATUSES) : 'unconfigured',
    verifiedAt: dto.verified_at,
    lastError: dto.last_error
});

export const toAudienceQueryDto = (selection: AudienceSelection, query?: AudienceCustomersQuery): AudienceQueryDto => ({
    marketplace_ids: selection.locationIds.join(','),
    population: selection.audience,
    ...(selection.customerIds !== null
        ? { customer_ids: selection.customerIds }
        : {
              criteria: selection.criteria,
              ...(selection.tags.length > 0 && { args: selection.tags.join(',') }),
              ...(selection.excludeIds.length > 0 && { exclude_ids: selection.excludeIds })
          }),
    ...(query && { page: query.page, size: query.size }),
    ...(query?.search && { search: query.search }),
    ...(query?.locationIds?.length && { filter_marketplace_ids: query.locationIds.join(',') })
});

export const convertToAudienceEstimate = (dto: AudienceEstimateDto): AudienceEstimate => ({
    totalRecipients: dto.total_recipients,
    duplicatesRemoved: dto.duplicates_removed,
    unsubscribedExcluded: dto.unsubscribed_excluded,
    byLocation: dto.by_location.map(row => ({
        locationId: row.marketplace_id,
        locationName: row.location_name ?? '',
        recipients: row.recipients
    }))
});

export const convertToGroupCounters = (dto: AudienceGroupCountersDto): AudienceGroupCounters => ({
    all: dto.all,
    byTag: dto.by_tag.map(row => ({ tag: row.tag, name: row.name, count: row.records }))
});

export const convertToCustomersPage = (dto: AudienceCustomersDto): AudienceCustomersPage => ({
    customers: dto.customers.map(row => ({
        id: row.id,
        firstName: row.first_name ?? '',
        lastName: row.last_name ?? '',
        email: row.email ?? '',
        locationId: row.marketplace_id,
        locationName: row.location_name ?? ''
    })),
    totalRecords: dto.total_records,
    totalPages: dto.total_pages ?? 1
});

export const convertToEditorConfig = (dto: EmailEditorConfigDto): EmailEditorConfig => ({
    projectId: dto.project_id,
    userId: dto.user_id,
    signature: dto.signature,
    mergeTags: dto.merge_tags ?? []
});

export const convertToTemplateDetail = ({ email_template: dto }: EmailTemplateDetailDto): EmailTemplateDetail => ({
    id: dto.id,
    name: dto.name,
    category: dto.category,
    locationId: dto.marketplace_id ?? null,
    system: dto.system,
    editable: dto.editable,
    html: dto.html ?? '',
    designJson: dto.design_json
});

export const toTemplateSaveDto = (input: EmailTemplateSaveInput): EmailTemplateSaveDto => ({
    email_template: {
        name: input.name,
        html: input.html,
        design_json: input.designJson,
        category: input.category
    }
});
